import {
  MASTER_COLUMN_KEY_PATTERN,
  MASTER_ID_PATTERN,
  MASTER_TEMPLATE_MAX_COLUMNS,
  canonicalMasterValue,
  type MasterAttrProblem,
  type MasterAttrs,
  type MasterAttrValue,
  type MasterColumnType,
  type MasterList,
  type MasterTemplate,
  type MasterTemplateColumn,
  type MasterValueType,
  type NormalizedTxn,
} from '@kkp-fraud/shared';
import { FIELD_KINDS, FIELD_NAMES, type FieldName } from './fields';

/**
 * Master Management's pure half: what the create/edit dialogs check before they send, and how
 * the screen prints a list. The admin service decides (`dotnet/admin-api`, `MasterRules`); these
 * mirror its rules so an operator sees a problem before a round trip, not instead of one.
 */

export const MASTER_NAME_MAX = 100;
export const MASTER_DESCRIPTION_MAX = 500;

export type MasterFormProblem = 'id' | 'name' | 'name_long' | 'description_long';

/** The fields a list of this type may be matched against: same kind, never boolean. */
export function fieldsFor(valueType: MasterValueType): FieldName[] {
  return FIELD_NAMES.filter((f) => FIELD_KINDS[f] === valueType);
}

export interface MasterForm {
  id: string;
  name: string;
  description: string;
  valueType: MasterValueType;
  field: keyof NormalizedTxn | '';
}

export function masterFormProblems(form: MasterForm, checkId: boolean): MasterFormProblem[] {
  const out: MasterFormProblem[] = [];
  if (checkId && !MASTER_ID_PATTERN.test(form.id.trim())) out.push('id');
  if (form.name.trim() === '') out.push('name');
  else if (form.name.trim().length > MASTER_NAME_MAX) out.push('name_long');
  if (form.description.trim().length > MASTER_DESCRIPTION_MAX) out.push('description_long');
  return out;
}

// ---- R11 master templates -------------------------------------------------------------------
// Mirrors `MasterTemplateRules` in dotnet/admin-api (the service decides; these only let the
// dialogs flag a problem before the round trip).

export const TEMPLATE_MAX_PATTERN = 200;
export const TEMPLATE_MAX_LABEL = 100;
export const ATTR_MAX_TEXT = 256;
export const COLUMN_TYPES: readonly MasterColumnType[] = ['string', 'number', 'date', 'boolean'];

export interface TemplateForm {
  id: string;
  name: string;
  description: string;
  keyType: MasterValueType;
  keyPattern: string;
  field: keyof NormalizedTxn | '';
  columns: MasterTemplateColumn[];
}

export type TemplateFormProblem =
  | 'id'
  | 'name'
  | 'name_long'
  | 'description_long'
  | 'field'
  | 'pattern'
  | 'column_key'
  | 'column_dup'
  | 'column_label'
  | 'columns_many';

/** Null when `pattern` compiles as a JavaScript regular expression (the service checks it again with .NET's). */
export function patternProblem(pattern: string): string | null {
  try {
    new RegExp(pattern);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

export function templateFormProblems(form: TemplateForm, checkId: boolean): TemplateFormProblem[] {
  const out: TemplateFormProblem[] = [];
  if (checkId && !MASTER_ID_PATTERN.test(form.id.trim())) out.push('id');
  if (form.name.trim() === '') out.push('name');
  else if (form.name.trim().length > MASTER_NAME_MAX) out.push('name_long');
  if (form.description.trim().length > MASTER_DESCRIPTION_MAX) out.push('description_long');
  if (form.field === '' || FIELD_KINDS[form.field] !== form.keyType) out.push('field');
  if (form.keyPattern !== '' && (form.keyPattern.length > TEMPLATE_MAX_PATTERN || patternProblem(form.keyPattern) !== null)) out.push('pattern');
  if (form.columns.length > MASTER_TEMPLATE_MAX_COLUMNS) out.push('columns_many');
  const keys = form.columns.map((c) => c.key.trim());
  if (keys.some((k) => !MASTER_COLUMN_KEY_PATTERN.test(k) || k === 'value' || k === 'note')) out.push('column_key');
  if (new Set(keys).size !== keys.length) out.push('column_dup');
  if (form.columns.some((c) => c.label.trim().length > TEMPLATE_MAX_LABEL)) out.push('column_label');
  return out;
}

/** Whether a canonical key matches the template's pattern — the WHOLE key, as the service checks it. */
export function keyMatches(template: Pick<MasterTemplate, 'keyPattern'> | null | undefined, key: string): boolean {
  if (!template?.keyPattern) return true;
  try {
    return new RegExp(`^(?:${template.keyPattern})$`).test(key);
  } catch {
    return true; // the service decides
  }
}

/**
 * A column's value as the service stores it, from what was typed: `undefined` = blank (no
 * value), or the problem. Text trimmed; a number any finite number; a date `yyyy-MM-dd`; a
 * yes/no true/false, yes/no, 1/0, ใช่/ไม่ใช่.
 */
export function parseAttr(
  column: MasterTemplateColumn,
  raw: string,
): { ok: true; value: MasterAttrValue | undefined } | { ok: false; reason: MasterAttrProblem } {
  const s = raw.replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
  if (s === '') return column.required ? { ok: false, reason: 'attr_required' } : { ok: true, value: undefined };
  switch (column.type) {
    case 'number': {
      const checked = canonicalMasterValue('number', s);
      return checked.ok ? { ok: true, value: Number(checked.value) } : { ok: false, reason: 'attr_not_a_number' };
    }
    case 'date': {
      const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00Z`) : null;
      return d && !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
        ? { ok: true, value: s }
        : { ok: false, reason: 'attr_not_a_date' };
    }
    case 'boolean': {
      const v = s.toLowerCase();
      if (['true', 'yes', 'y', '1', 'ใช่'].includes(v)) return { ok: true, value: true };
      if (['false', 'no', 'n', '0', 'ไม่ใช่'].includes(v)) return { ok: true, value: false };
      return { ok: false, reason: 'attr_not_a_boolean' };
    }
    default:
      return s.length > ATTR_MAX_TEXT ? { ok: false, reason: 'attr_too_long' } : { ok: true, value: s };
  }
}

/** Every column of `template` from the typed text: the attrs to send, or the first problem (column key + reason). */
export function parseAttrs(
  template: MasterTemplate,
  raw: Record<string, string>,
): { ok: true; attrs: MasterAttrs } | { ok: false; column: string; reason: MasterAttrProblem } {
  const attrs: MasterAttrs = {};
  for (const c of template.columns) {
    const r = parseAttr(c, raw[c.key] ?? '');
    if (!r.ok) return { ok: false, column: c.key, reason: r.reason };
    if (r.value !== undefined) attrs[c.key] = r.value;
  }
  return { ok: true, attrs };
}

/** A stored value back as editable text (a yes/no as true/false). */
export function attrInput(v: MasterAttrValue | undefined): string {
  return v === undefined ? '' : String(v);
}

/** The templates a list with no template may take: the same key type and the same field. */
export function assignableTemplates(list: MasterList, templates: readonly MasterTemplate[]): MasterTemplate[] {
  return templates.filter((t) => t.keyType === list.valueType && list.field !== undefined && t.field === list.field);
}

export function templateFormOf(t?: MasterTemplate): TemplateForm {
  return {
    id: t?.id ?? '',
    name: t?.name ?? '',
    description: t?.description ?? '',
    keyType: t?.keyType ?? 'string',
    keyPattern: t?.keyPattern ?? '',
    field: t?.field ?? '',
    columns: t ? t.columns.map((c) => ({ ...c })) : [],
  };
}
